import { type MaybeRefOrGetter, computed, onMounted, onUnmounted, toValue, watch } from 'vue';

import { App, type BackButtonListenerEvent } from '@capacitor/app';
import { Toast } from '@capacitor/toast';
import { useEventListener } from '@vueuse/core';

type Listener = (event: BackButtonListenerEvent | KeyboardEvent) => boolean | undefined | Promise<boolean | undefined>;

/** When no page/overlay handlers claim back, hub can navigate (e.g. sub-app → welcome). */
export type UnresolvedBackFallback = () => boolean | Promise<boolean>;

const listeners: Listener[] = [];
let unresolvedBackFallback: UnresolvedBackFallback | null = null;

let lastBackButtonPress = 0;
/**
 * Capacitor Android fires Cap `backButton` and then document `backbutton` for one
 * physical press. Coalesce both into a single handleBackPress run.
 */
let backPressInFlight: Promise<boolean> | null = null;
let lastHandledAt = 0;
const BACK_PRESS_DEDUPE_MS = 250;

/**
 * Optional host fallback when no `useClose` handler claims the back press
 * (none attached, or all return falsy).
 * Return `true` if the back press was handled (e.g. navigated to hub welcome).
 * Return `false` to use the default double-back exit toast.
 */
export function setUnresolvedBackFallback(fallback: UnresolvedBackFallback | null): void {
	unresolvedBackFallback = fallback;
}

async function showExitToastOrExitApp(): Promise<void> {
	const BACK_BUTTON_DELAY = 2000;
	const now = Date.now();
	if (now - lastBackButtonPress < BACK_BUTTON_DELAY) {
		console.log('double back button press, exiting app');
		App.exitApp();
	} else {
		lastBackButtonPress = now;
		console.log('single back button press, showing toast');
		await Toast.show({
			text: 'Press back again to exit',
			duration: 'short',
			position: 'bottom',
		});
	}
}

const handleBackPress = async (event: BackButtonListenerEvent | KeyboardEvent): Promise<boolean> => {
	if (backPressInFlight) {
		return backPressInFlight;
	}

	if (Date.now() - lastHandledAt < BACK_PRESS_DEDUPE_MS) {
		return true;
	}

	backPressInFlight = (async () => {
		for (const [index, listener] of listeners.entries()) {
			console.log('calling listener #', index);
			try {
				if (await listener(event)) {
					console.log('listener #', index, 'returned true');
					lastBackButtonPress = 0;
					return true;
				}
			} catch (error) {
				console.error('listener #', index, 'threw error', error);
			}
		}

		if (unresolvedBackFallback) {
			try {
				if (await unresolvedBackFallback()) {
					lastBackButtonPress = 0;
					return true;
				}
			} catch (error) {
				console.error('unresolved back fallback threw', error);
			}
		}

		await showExitToastOrExitApp();
		return true;
	})();

	try {
		return await backPressInFlight;
	} finally {
		lastHandledAt = Date.now();
		backPressInFlight = null;
	}
};

App.addListener('backButton', async (event) => {
	await handleBackPress(event);
});

useEventListener(document, 'backbutton', (event: Event) => {
	event.preventDefault();
	void handleBackPress(event as KeyboardEvent);
});

useEventListener(document, 'keyup', async (event) => {
	// close triggered via Escape key on desktop
	if (event.key === 'Escape' && !event.ctrlKey && !event.metaKey && !event.altKey) {
		await handleBackPress(event);
		event.preventDefault();
		event.stopImmediatePropagation();
	}
});

export const useClose = (callback: Listener, enabled?: MaybeRefOrGetter<boolean>) => {
	const attach = () => {
		detach();
		listeners.unshift(callback);
	};
	const detach = () => {
		const index = listeners.indexOf(callback);
		if (index !== -1) {
			listeners.splice(index, 1);
		}
	};

	onMounted(() => {
		if (enabled == null || toValue(enabled)) {
			attach();
		}
	});

	onUnmounted(() => {
		detach();
	});

	watch(
		computed(() => toValue(enabled)),
		(enabled) => {
			if (enabled === true) {
				attach();
			} else if (enabled === false) {
				detach();
			}
		}
	);

	return {
		attach,
		detach,
	};
};
